import React, { useState, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Calendar as CalendarIcon, ArrowRight, Check, AlertCircle, Loader2 } from 'lucide-react';
import 'react-day-picker/dist/style.css';

/** Google Form backup – odoslanie rezervácie do Google Sheets po úspešnej rezervácii v kalendári */
const GOOGLE_FORM_ACTION_URL = process.env.REACT_APP_GOOGLE_FORM_ACTION_URL || '';
const GOOGLE_FORM_ENTRIES = {
  name: process.env.REACT_APP_GOOGLE_FORM_ENTRY_NAME || '',
  email: process.env.REACT_APP_GOOGLE_FORM_ENTRY_EMAIL || '',
  phone: process.env.REACT_APP_GOOGLE_FORM_ENTRY_PHONE || '',
  note: process.env.REACT_APP_GOOGLE_FORM_ENTRY_NOTE || '',
  date: process.env.REACT_APP_GOOGLE_FORM_ENTRY_DATE || '',
  time: process.env.REACT_APP_GOOGLE_FORM_ENTRY_TIME || '',
};

function submitToGoogleForm(data) {
  if (!GOOGLE_FORM_ACTION_URL) return;
  const form = document.createElement('form');
  form.action = GOOGLE_FORM_ACTION_URL;
  form.method = 'POST';
  form.target = 'google-form-backup-iframe';
  form.style.display = 'none';
  let hasFields = false;
  Object.entries(GOOGLE_FORM_ENTRIES).forEach(([fieldName, entryId]) => {
    if (!entryId) return;
    hasFields = true;
    const value = data[fieldName];
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = entryId;
    input.value = String(value ?? '');
    form.appendChild(input);
  });
  if (!hasFields) return;
  document.body.appendChild(form);
  form.submit();
  form.remove();
}

// Days of week (0=Sun) that have availability configured — kept in sync with env vars
// TUE=2, WED=3, THU=4
const AVAILABLE_DAYS_OF_WEEK = [2, 3, 4];
const MIN_ADVANCE_HOURS = 48;

/** Returns the earliest selectable date (today + 48h, rounded to start of that day) */
function getMinSelectableDate() {
  const d = new Date(Date.now() + MIN_ADVANCE_HOURS * 60 * 60 * 1000);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** True if a given date has no available day-of-week window */
function isDayBlocked(date) {
  const minDate = getMinSelectableDate();
  if (date < minDate) return true;
  return !AVAILABLE_DAYS_OF_WEEK.includes(date.getDay());
}

/** Find the first selectable date from today */
function getFirstAvailableDate() {
  const d = getMinSelectableDate();
  while (isDayBlocked(d)) {
    d.setDate(d.getDate() + 1);
  }
  return d;
}

const CustomCalendar = () => {
  const [selectedDate, setSelectedDate] = useState(() => getFirstAvailableDate());
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [view, setView] = useState('calendar');
  const [slots, setSlots] = useState([]);
  const [isDayUnavailable, setIsDayUnavailable] = useState(false);
  const [isTooSoon, setIsTooSoon] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState(null);
  const [bookingResult, setBookingResult] = useState(null);

  const dateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null;

  const handleDateSelect = (date) => {
    if (!date || isDayBlocked(date)) return;
    setSelectedDate(date);
  };

  useEffect(() => {
    if (!dateStr) return;

    setSlotsLoading(true);
    setSlotsError(null);
    setSelectedSlot(null);
    setIsDayUnavailable(false);
    setIsTooSoon(false);

    fetch(`/api/calendar/freebusy?date=${encodeURIComponent(dateStr)}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.isDayUnavailable) {
          setIsDayUnavailable(true);
          setSlots([]);
        } else if (data.tooSoon) {
          setIsTooSoon(true);
          setSlots([]);
        } else if (data.slots && Array.isArray(data.slots)) {
          setSlots(data.slots);
        } else {
          setSlots([]);
        }
      })
      .catch(() => {
        setSlotsError('Nepodarilo sa načítať voľné termíny.');
        setSlots([]);
      })
      .finally(() => setSlotsLoading(false));
  }, [dateStr]);

  const handleSlotClick = (slot) => {
    if (slot.available) {
      setSelectedSlot(slot);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    setBookingError(null);

    const form = e.target;
    const name = form.querySelector('input[name="name"]')?.value?.trim();
    const email = form.querySelector('input[name="email"]')?.value?.trim();
    const phone = form.querySelector('input[name="phone"]')?.value?.trim();
    const note = form.querySelector('textarea[name="note"]')?.value?.trim() || '';

    if (!name || !email || !phone || !dateStr || !selectedSlot) {
      setBookingError('Vyplňte prosím všetky povinné polia.');
      return;
    }

    setBookingLoading(true);

    try {
      const res = await fetch('/api/calendar/book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: dateStr,
          time: selectedSlot.time,
          name,
          email,
          phone,
          note,
        }),
      });

      let data;
      try {
        data = await res.json();
      } catch {
        setBookingError(
          'API nedostupné. Lokálne spúšťajte: vercel dev (nie npm start).'
        );
        return;
      }

      if (!res.ok) {
        setBookingError(data.error || 'Rezervácia zlyhala.');
        return;
      }

      setBookingResult({ email, meetLink: data.meetLink, htmlLink: data.htmlLink });
      submitToGoogleForm({
        name,
        email,
        phone,
        note,
        date: dateStr,
        time: selectedSlot.time,
      });
      setView('success');
    } catch {
      setBookingError(
        'Chyba pripojenia. Lokálne spúšťajte: vercel dev (nie npm start).'
      );
    } finally {
      setBookingLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden border border-gray-100 min-h-0 md:min-h-[550px] flex flex-col md:flex-row relative h-full w-full max-w-full min-w-0">
      <iframe
        name="google-form-backup-iframe"
        title="Google Form backup"
        style={{ display: 'none' }}
        aria-hidden="true"
      />
      <style>{`
        .rdp {
          --rdp-cell-size: 40px;
          --rdp-accent-color: #FF7A4A;
          --rdp-background-color: #e7f5ff;
          margin: 0;
        }
        .rdp-day_selected:not([aria-disabled='true']) { 
          background-color: var(--rdp-accent-color); 
          font-weight: bold;
        }
        .rdp-day_today {
          font-weight: bold;
          color: #1F3954;
        }
        .rdp-button:hover:not([aria-disabled='true']) {
          background-color: #FDDACD;
        }
      `}</style>

      {/* Left Panel: Calendar & Info */}
      <div className="md:w-5/12 bg-brand-dark-blue text-white p-4 sm:p-7 flex flex-col justify-between relative overflow-x-clip">
        <div className="absolute top-0 right-0 w-64 h-64 bg-brand-orange/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-brand-baby-blue/10 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4 pointer-events-none"></div>

        <div className="relative z-10">
          <h3 className="text-2xl font-heading font-bold mb-3">Rezervácia konzultácie</h3>
          <p className="text-brand-gray/80 text-sm mb-8">Vyberte si termín, ktorý vám najviac vyhovuje.</p>

          <div className="space-y-5">
            <div className="flex items-start gap-4">
              <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                <Clock className="text-brand-orange" size={18} />
              </div>
              <div>
                <p className="font-bold text-base">30 minút</p>
                <p className="text-xs text-brand-gray/60">Trvanie konzultácie</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                <CalendarIcon className="text-brand-baby-blue" size={18} />
              </div>
              <div>
                <p className="font-bold text-base">Google Meet</p>
                <p className="text-xs text-brand-gray/60">Online videohovor</p>
              </div>
            </div>
          </div>
        </div>

        {selectedDate && selectedSlot && (
          <div className="mt-8 pt-8 border-t border-white/10 animate-fade-in-up">
            <p className="text-xs text-brand-gray/60 uppercase tracking-widest mb-2">Vybraný termín</p>
            <div className="text-xl font-bold text-brand-orange">
              {format(selectedDate, 'd. MMMM', { locale: cs })}
            </div>
            <div className="text-3xl font-bold">{selectedSlot.time}</div>
          </div>
        )}
      </div>

      {/* Right Panel: Content */}
      <div className="md:w-7/12 p-4 sm:p-7 relative overflow-y-auto min-w-0">
        <AnimatePresence mode="wait">
          {view === 'calendar' && (
            <motion.div
              key="calendar-view"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="h-full flex flex-col"
            >
              <div className="mb-6 flex justify-center bg-gray-50 rounded-2xl p-3">
                <DayPicker
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  locale={cs}
                  disabled={(date) => isDayBlocked(date)}
                  footer={
                    <p className="text-xs text-center mt-2 text-gray-400">
                      {selectedDate
                        ? `Vybraný dátum: ${format(selectedDate, 'PP', { locale: cs })}`
                        : 'Vyberte dátum'}
                    </p>
                  }
                />
              </div>

              <div className="flex-grow">
                <h4 className="font-bold text-brand-dark-blue mb-4 text-base flex items-center">
                  <Clock size={16} className="mr-2 text-brand-orange" />
                  Voľné časy {selectedDate && `pre ${format(selectedDate, 'd. MMMM', { locale: cs })}`}
                </h4>

                {slotsLoading ? (
                  <div className="h-32 flex flex-col items-center justify-center text-gray-400 border-2 border-dashed border-gray-100 rounded-xl">
                    <Loader2 size={24} className="mb-2 animate-spin" />
                    <p className="text-sm">Načítavam termíny...</p>
                  </div>
                ) : slotsError ? (
                  <div className="h-32 flex flex-col items-center justify-center text-gray-500 border-2 border-dashed border-gray-200 rounded-xl p-4">
                    <AlertCircle size={24} className="mb-2 text-amber-500" />
                    <p className="text-sm text-center">{slotsError}</p>
                  </div>
                ) : isDayUnavailable ? (
                  <div className="h-32 flex flex-col items-center justify-center text-gray-400 border-2 border-dashed border-gray-100 rounded-xl px-4">
                    <AlertCircle size={24} className="mb-2 opacity-40" />
                    <p className="text-sm text-center font-medium">V tento deň nie sú konzultácie</p>
                    <p className="text-xs text-center mt-1 text-gray-400">Dostupné dni: Utorok, Streda, Štvrtok</p>
                  </div>
                ) : isTooSoon ? (
                  <div className="h-32 flex flex-col items-center justify-center text-gray-400 border-2 border-dashed border-gray-100 rounded-xl px-4">
                    <AlertCircle size={24} className="mb-2 text-amber-400" />
                    <p className="text-sm text-center font-medium">Rezervácia je príliš skoro</p>
                    <p className="text-xs text-center mt-1 text-gray-400">Termín musí byť aspoň 48 hodín vopred</p>
                  </div>
                ) : slots.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {slots.map((slot, idx) => (
                      <button
                        key={idx}
                        disabled={!slot.available}
                        onClick={() => handleSlotClick(slot)}
                        className={`
                          py-3 px-2 rounded-xl border text-sm font-medium transition-all duration-200
                          ${!slot.available
                            ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed'
                            : selectedSlot === slot
                              ? 'bg-brand-dark-blue text-white border-brand-dark-blue shadow-lg scale-105'
                              : 'bg-white text-gray-700 border-gray-200 hover:border-brand-orange hover:text-brand-orange hover:shadow-md'}
                        `}
                      >
                        {slot.time}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="h-32 flex flex-col items-center justify-center text-gray-400 border-2 border-dashed border-gray-100 rounded-xl">
                    <AlertCircle size={24} className="mb-2 opacity-50" />
                    <p className="text-sm">Pre tento deň nie sú voľné termíny.</p>
                  </div>
                )}
              </div>

              <div className="mt-8 flex justify-end">
                <button
                  onClick={() => selectedSlot && setView('form')}
                  disabled={!selectedSlot}
                  className={`
                    flex items-center gap-2 py-3 px-8 rounded-full font-bold text-white transition-all duration-300
                    ${selectedSlot
                      ? 'bg-brand-orange hover:bg-orange-600 shadow-lg hover:shadow-orange-500/30'
                      : 'bg-gray-200 cursor-not-allowed'}
                  `}
                >
                  Pokračovať
                  <ArrowRight size={18} />
                </button>
              </div>
            </motion.div>
          )}

          {view === 'form' && (
            <motion.div
              key="form-view"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="h-full flex flex-col"
            >
              <button
                onClick={() => setView('calendar')}
                className="text-sm text-gray-400 hover:text-brand-dark-blue mb-6 flex items-center"
              >
                ← Späť na výber termínu
              </button>

              <h3 className="text-2xl font-bold text-brand-dark-blue mb-6">Vaše údaje</h3>

              <form onSubmit={handleBooking} className="space-y-4 flex-grow">
                {bookingError && (
                  <div className="p-3 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm">
                    {bookingError}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meno a priezvisko</label>
                  <input
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-brand-orange focus:ring-1 focus:ring-brand-orange outline-none transition-all"
                    placeholder="Jozef Novák"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-brand-orange focus:ring-1 focus:ring-brand-orange outline-none transition-all"
                    placeholder="jozef@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefón</label>
                  <input
                    name="phone"
                    type="tel"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-brand-orange focus:ring-1 focus:ring-brand-orange outline-none transition-all"
                    placeholder="+421 900 000 000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Poznámka (nepovinné)</label>
                  <textarea
                    name="note"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-brand-orange focus:ring-1 focus:ring-brand-orange outline-none transition-all h-24 resize-none"
                    placeholder="O čom by ste sa chceli poradiť?"
                  />
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={bookingLoading}
                    className="w-full bg-brand-dark-blue text-white font-bold py-4 rounded-xl hover:bg-opacity-90 transition-all shadow-lg hover:shadow-xl flex justify-center items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {bookingLoading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        <span>Rezervujem...</span>
                      </>
                    ) : (
                      <>
                        <span>Rezervovať termín</span>
                        <ArrowRight size={18} />
                      </>
                    )}
                  </button>
                </div>
                <p className="text-xs text-center text-gray-400 mt-4">
                  Kliknutím súhlasíte so spracovaním osobných údajov.
                </p>
              </form>
            </motion.div>
          )}

          {view === 'success' && bookingResult && (
            <motion.div
              key="success-view"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="h-full flex flex-col items-center justify-center text-center p-8"
            >
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <Check size={48} className="text-green-500" />
              </div>
              <h3 className="text-3xl font-bold text-brand-dark-blue mb-4">Rezervácia úspešná!</h3>
              <p className="text-gray-500 mb-4 max-w-sm">
                Ďakujem za vašu rezerváciu. Na email <strong className="text-gray-700">{bookingResult.email}</strong>{' '}
                vám boli odoslané detaily stretnutia a odkaz na Google Meet.
              </p>
              {bookingResult.meetLink && (
                <a
                  href={bookingResult.meetLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mb-6 px-6 py-3 rounded-xl bg-brand-orange text-white font-bold hover:bg-orange-600 transition-colors"
                >
                  Otvoriť Google Meet
                </a>
              )}
              <button
                onClick={() => window.location.reload()}
                className="text-brand-orange font-bold hover:underline"
              >
                Rezervovať ďalší termín
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CustomCalendar;
